// src/components/investigations/InvestigationList.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { format } from 'date-fns';

const InvestigationList = ({ limit, onSelect, refreshKey }) => {
  const { t } = useTranslation();
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', priority: '' });
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest' or 'oldest'
  const isLoadingRef = useRef(false);
  const timeoutRef = useRef(null);
  const lastLoadTimeRef = useRef(0);
  const DEBOUNCE_MS = 3000; // Minimum 3 seconds between calls

  const loadInvestigations = useCallback(async (force = false) => {
    const now = Date.now();
    // Debounce: don't call if we're already loading or if called too recently
    if (!force && (isLoadingRef.current || (now - lastLoadTimeRef.current < DEBOUNCE_MS))) {
      return;
    }

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the actual API call
    timeoutRef.current = setTimeout(async () => {
      try {
        isLoadingRef.current = true;
        lastLoadTimeRef.current = Date.now();
        setLoading(true);
        const params = new URLSearchParams();
        if (limit) params.append('limit', limit);
        if (filter.status) params.append('status', filter.status);
        if (filter.priority) params.append('priority', filter.priority);

        const response = await api.get(`/api/journalist/investigations?${params.toString()}`);
        setInvestigations(response.data.investigations || []);
      } catch (error) {
        console.error('Failed to load investigations:', error);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    }, force ? 0 : 500); // Immediate if forced, 500ms debounce otherwise
  }, [limit, filter]);

  useEffect(() => {
    loadInvestigations(refreshKey > 0);
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, filter, refreshKey]);

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400',
      completed: 'bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light',
      archived: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300',
      on_hold: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-400',
    };
    return colors[status] || colors.active;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300',
      medium: 'bg-shayek-primary/10 dark:bg-shayek-primary/20 text-shayek-primary dark:text-shayek-red-light',
      high: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-400',
      urgent: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400',
    };
    return colors[priority] || colors.medium;
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shayek-primary dark:border-shayek-red-light mx-auto"></div>
        <p className="mt-2 text-sm text-gray-600 dark:text-shayek-dark-600">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!limit && (
        <div className="flex gap-4 mb-4">
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="px-3 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg text-sm bg-white dark:bg-shayek-dark-50 text-gray-900 dark:text-shayek-dark-900"
          >
            <option value="">{t('journalist.investigations.filters.allStatuses')}</option>
            <option value="active">{t('journalist.investigations.status.active')}</option>
            <option value="completed">{t('journalist.investigations.status.completed')}</option>
            <option value="on_hold">{t('journalist.investigations.status.on_hold')}</option>
            <option value="archived">{t('journalist.investigations.status.archived')}</option>
          </select>
          <select
            value={filter.priority}
            onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
            className="px-3 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg text-sm bg-white dark:bg-shayek-dark-50 text-gray-900 dark:text-shayek-dark-900"
          >
            <option value="">{t('journalist.investigations.filters.allPriorities')}</option>
            <option value="low">{t('journalist.investigations.priority.low')}</option>
            <option value="medium">{t('journalist.investigations.priority.medium')}</option>
            <option value="high">{t('journalist.investigations.priority.high')}</option>
            <option value="urgent">{t('journalist.investigations.priority.urgent')}</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg text-sm bg-white dark:bg-shayek-dark-50 text-gray-900 dark:text-shayek-dark-900"
          >
            <option value="newest">{t('journalist.investigations.filters.newest') || 'Newest First'}</option>
            <option value="oldest">{t('journalist.investigations.filters.oldest') || 'Oldest First'}</option>
          </select>
        </div>
      )}

      {investigations.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-shayek-dark-500">
          <p>{t('journalist.investigations.empty')}</p>
        </div>
      ) : (

        <div className="space-y-3">
          {[...investigations].sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
          }).map((investigation) => (
            <div
              key={investigation.id}
              onClick={() => onSelect && onSelect(investigation)}
              className="bg-white dark:bg-shayek-dark-50 border border-gray-200 dark:border-shayek-dark-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-shayek-dark-900 mb-2">{investigation.title}</h3>
                  {investigation.description && (
                    <p className="text-sm text-gray-600 dark:text-shayek-dark-600 mb-3 line-clamp-2">{investigation.description}</p>
                  )}
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(investigation.status)}`}>
                      {t(`journalist.investigations.status.${investigation.status}`)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(investigation.priority)}`}>
                      {t(`journalist.investigations.priority.${investigation.priority}`)}
                    </span>
                    {investigation.related_report_ids && investigation.related_report_ids.length > 0 && (
                      <span className="text-xs text-gray-500 dark:text-shayek-dark-500">
                        {investigation.related_report_ids.length} {t('journalist.investigations.reports')}
                      </span>
                    )}
                    <span className="text-xs text-gray-500 dark:text-shayek-dark-500">
                      {format(new Date(investigation.created_at), 'MMM d, yyyy')}
                    </span>
                  </div>
                </div>
                <svg
                  className="w-5 h-5 text-gray-400 dark:text-shayek-dark-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InvestigationList;

